/**
 * Sélecteur visuel d'icône CMS (grille dans un popover).
 */
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import {
  CMS_ICONS,
  CMS_ICON_NAMES,
  resolveCmsIcon,
  type CmsIconName,
} from "@/lib/cms-puck/blocks/icons";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";

type Props = {
  value: string | undefined;
  onChange: (value: string) => void;
  allowEmpty?: boolean;
  emptyLabel?: string;
};

export function IconPickerField({
  value,
  onChange,
  allowEmpty = false,
  emptyLabel,
}: Props) {
  const cmsNew = useCmsNew();
  const resolvedEmptyLabel =
    emptyLabel ?? cmsNew("cms.new.pickers.icon.none", "Aucune");
  const current = (value ?? "") as CmsIconName | "";
  const SelectedIcon = current ? resolveCmsIcon(current) : null;
  const displayLabel =
    current ||
    (allowEmpty
      ? resolvedEmptyLabel
      : cmsNew("cms.new.pickers.icon.choose", "Choisir une icône"));

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="h-10 w-full justify-between gap-2 px-3"
        >
          <span className="flex min-w-0 items-center gap-2">
            {SelectedIcon ? (
              <SelectedIcon className="h-5 w-5 shrink-0 text-slate-700" />
            ) : (
              <span className="h-5 w-5 shrink-0 rounded border border-dashed border-slate-300" />
            )}
            <span
              className="truncate text-sm text-slate-700"
              title={displayLabel}
            >
              {displayLabel}
            </span>
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-2">
        <div className="grid grid-cols-6 gap-1">
          {allowEmpty ? (
            <button
              type="button"
              title={resolvedEmptyLabel}
              aria-label={resolvedEmptyLabel}
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-md border text-slate-400 transition-colors",
                current === ""
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 bg-white hover:bg-slate-50",
              )}
              onClick={() => onChange("")}
            >
              <span className="text-[10px] font-medium">—</span>
            </button>
          ) : null}
          {CMS_ICON_NAMES.map((name) => {
            const Icon = CMS_ICONS[name];
            const active = current === name;
            return (
              <button
                key={name}
                type="button"
                title={name}
                aria-label={name}
                aria-pressed={active}
                className={cn(
                  "flex h-10 w-10 items-center justify-center rounded-md border transition-colors",
                  active
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
                )}
                onClick={() => onChange(name)}
              >
                <Icon className="h-5 w-5" />
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
